import React from 'react';
import { X, AlertTriangle, Info, Trash2, CheckCircle } from 'lucide-react';

/**
 * Reusable confirmation modal to replace browser confirm() dialogs
 */
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default', // 'default' | 'danger' | 'warning' | 'info'
  isLoading = false
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    default: {
      icon: CheckCircle,
      iconBg: 'bg-brand-soft dark:bg-blue-900/40',
      iconColor: 'text-brand dark:text-blue-400',
      confirmBtn: 'bg-brand hover:bg-brand-hover dark:bg-brand dark:hover:bg-brand-hover text-white'
    },
    danger: {
      icon: Trash2,
      iconBg: 'bg-danger-soft dark:bg-red-900/40',
      iconColor: 'text-danger dark:text-red-400',
      confirmBtn: 'bg-danger hover:bg-danger-hover dark:bg-danger dark:hover:bg-danger-hover text-white'
    },
    warning: {
      icon: AlertTriangle,
      iconBg: 'bg-warning-soft dark:bg-amber-900/40',
      iconColor: 'text-warning dark:text-amber-400',
      confirmBtn: 'bg-warning hover:bg-warning-hover dark:bg-warning dark:hover:bg-warning-hover text-white'
    },
    info: {
      icon: Info,
      iconBg: 'bg-brand-soft dark:bg-purple-900/40',
      iconColor: 'text-brand dark:text-purple-400',
      confirmBtn: 'bg-brand hover:bg-brand-hover dark:bg-brand dark:hover:bg-brand-hover text-white'
    }
  };

  const style = variantStyles[variant] || variantStyles.default;
  const IconComponent = style.icon;

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-surface rounded-xl shadow-lg max-w-md w-full mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-hairline"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/10">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${style.iconBg}`}>
              <IconComponent className={`w-5 h-5 ${style.iconColor}`} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-ink-subtle" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <p className="text-gray-600 dark:text-ink-subtle leading-relaxed">{message}</p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-white/5 border-t border-gray-100 dark:border-white/10">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-3.5 py-2 text-sm font-medium text-gray-700 dark:text-white bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/15 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-3.5 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 ${style.confirmBtn}`}
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
